<script setup>
import { supportCss, isObject } from "@common/utils";
import NioImage from "busComponent/src/baseComponents/NioImage.vue";
import NioVideo from "./NioVideo.vue";
import NioVideoPlayer from "./NioVideoPlayer.vue";
import { nextTick } from "vue";
import { v4 as uuid } from "uuid";
import { useVideoStore } from "../store/video";

const attrs = useAttrs();
const emit = defineEmits(["videoClick"]);
const props = defineProps({
  imgList: {
    type: Array,
    default: () => [],
  },
  size: {
    type: String,
    default: "contain",
  },
  enableNioImage: {
    type: Boolean,
    default: false,
  },
  dark: [Boolean, Number],
  clip: {},
  supportVideo: {
    type: Boolean,
    default: false,
  },
});
const imgFail = ref(false);

// 处理为数组形式，方便展开
const clip = computed(
  () => props.clip && (Array.isArray(props.clip) ? props.clip : [props.clip])
);
const swiperRef = ref();
const classMap = computed(() => ({
  "nio-carousel-img": true,
  "nio-dark": props.dark,
  "nio-hide": props.imgList.length <= 1, // 按钮不能v-if，数据切换会造成swiper无法找到按钮元素
  "nio-no-objectFit": !supportCss("objectFit"), // 是否支持 object-fit
}));

const videoPlayRef = ref();

const $swiper = computed(() => swiperRef.value?.$swiper);
const videoStore = useVideoStore();

watch(
  () => props.imgList,
  (newVal, oldVal) => {
    props.supportVideo && videoStore.$reset();
    if (newVal?.length !== oldVal?.length) {
      nextTick(() => {
        $swiper.value?.slideToLoop?.(0, 0);
        slideChange();
      });
    } else {
      nextTick(() => {
        slideChange();
      });
    }
  },
  {
    immediate: true,
  }
);

const materialList = computed(() => {
  // 如果是字符串数组类型的话则直接返回
  if (props.imgList.length > 0 && typeof props.imgList[0] === "string") {
    return props.imgList.map((item) => {
      return {
        type: "img",
        material: item,
      };
    });
  }
  return props.imgList.map((item) => {
    if (item.type == "video" && props.supportVideo) {
      item.id = `video-${uuid()}`;
    }
    return item;
  });
});

// 配置参数参考：https://swiper.com.cn/api/
const swiperOptions = computed(() => ({
  observer: true, // 轮播数据变化会自动初始化swiper
  loop: true, // 一张图的话，禁止轮播
  // cssMode: true, // 体验好，但上下滑动无法响应
  mousewheel: true,
  threshold: 20,
  navigation: {
    nextEl: ".nio-carousel-next",
    prevEl: ".nio-carousel-prev",
    disabledClass: "nio-disable",
  },
  pagination: {
    el: ".nio-pagination",
    clickable: true,
  },
  slidesPerView: 1,
  updateOnWindowResize: true,
  resizeObserver: true,
  on: {
    init: function () {
      console.log("---初始化swiper---");
    },
    resize: () => {
      setTimeout(() => {
        swiperRef.value?.updateSwiper();
      }, 200);
    },
    slideChange: function () {
      // 在slide切换后重新绑定事件
      nextTick(() => {
        const activeSlide = this.slides[this.activeIndex];
        const playButtons = activeSlide.querySelectorAll(".play-icon-container");
        playButtons.forEach((button) => {
          const imgsEl = button?.closest(".video-wrap")?.closest(".imgs-wrap");
          if (imgsEl?.closest(".swiper-slide-duplicate")) {
            console.log("---duplicate---",this.realIndex,this.activeIndex);
            button.onclick = (e) => {
              e.stopPropagation();
              const index = this.realIndex;
              handleVideoClick(materialList.value[index]);
            };
            return;
          }
        });
      });
    },
  },
  ...attrs, // 合并外层配置
}));

const imgLoadFail = () => {
  imgFail.value = true;
};
const imgLoadSucc = () => {
  imgFail.value = false;
};
const materialImgUrl = (material) => {
  let url = "";
  if (isObject(material)) {
    url = material?.type == "video" ? material?.img : material?.material;
  } else {
    url = material;
  }
  return clip.value ? imgClipPlan(url, ...clip.value) : url;
};

const handleDynamicRef = (el, id, index) => {
  if (el) {
    videoStore.setVideoRefToMap(el, id);
  }
};

const slideChange = () => {
  // const index = $swiper.value?.realIndex;
  // console.log('---active index---',$swiper.value?.activeIndex,index);
  // if(index == 0){
  //   setTimeout(() => {
  //     $swiper.value?.slideToLoop?.(0, 0);
  //   }, 300);
  //   return
  // }else if(index == materialList.value.length - 1){
  //   setTimeout(() => {
  //     $swiper.value?.slideToLoop?.(materialList.value.length - 1, 0);
  //   }, 300);
  //   return
  // }
  // if (props.supportVideo) {
  //   setTimeout(() => {
  //     const index = $swiper.value?.realIndex;
  //     videoStore?.playWithPause?.(materialList.value[index]?.id);
  //   }, 300);
  // }
};

const modeTransform = (size) => {
  switch (size) {
    case "contain":
      return "aspectFit";
    case "cover":
      return "aspectFill";
    default:
      return "aspectFill";
  }
};

const handleVideoClick = (material) => {
  const index = $swiper.value?.realIndex;
  console.log(
    "---activeindex---",
    $swiper.value?.activeIndex,
    "---realIndex---",
    index
  );
  console.log("---material---", material);
  emit("videoClick", material);
};
</script>

<template>
  <div :class="classMap" @mousewheel.stop>
    <swiper
      class="nio-swiper"
      :options="swiperOptions"
      ref="swiperRef"
      @slideChange="slideChange"
    >
      <swiper-slide
        v-for="(material, index) in materialList"
        :key="(material?.material || material) + index"
      >
        <div class="imgs-wrap" v-if="isObject(material)">
          <div
            class="video-wrap"
            v-if="
              material?.type == 'video' && supportVideo && material?.material
            "
          >
            <nio-image
              :src="materialImgUrl(material)"
              :wrapper-style="{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }"
              external-fail-img-class="img-no-size"
              :mode="size ? modeTransform(size) : 'aspectFill'"
            />
            <div class="play-icon-container">
              <div
              class="play-icon-wrap"
              @click.stop="handleVideoClick(material)"
            >
              <nio-icon class="play-icon" type="play_filled_xl" raw></nio-icon>
            </div>
            </div>
           
          </div>
          <nio-image
            v-else
            :src="materialImgUrl(material)"
            :wrapper-style="{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }"
            external-fail-img-class="img-no-size"
            :mode="size ? modeTransform(size) : 'aspectFill'"
          />
          <!-- <img
            v-else-if="materialImgUrl(material)"
            :src="materialImgUrl(material)"
            :style="`object-fit: ${size}`"
          /> -->
        </div>
        <!-- <div class="imgs-wrap" v-else>
          <img v-if="!enableNioImage && url" :src="clip? imgClipPlan(url, ...clip): url" :style="`object-fit: ${size}`" >
          <nio-image
              v-else-if="enableNioImage"
              :src="clip? imgClipPlan(url, ...clip): url"
              :wrapper-style="{
                display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
              }"
              external-fail-img-class="img-no-size"
              mode="aspectFill"
          />
        </div> -->
      </swiper-slide>
      <!-- <nio-video-player ref="videoPlayRef" /> -->

      <nio-button
        slot="button-prev"
        class="nio-carousel-prev"
        icon="left"
        circle
      />
      <nio-button
        slot="button-next"
        class="nio-carousel-next"
        icon="right"
        circle
      />

      <div slot="pagination" class="nio-pagination"></div>
    </swiper>
  </div>
</template>

<style lang="scss" scoped>
.nio-carousel-img,
.nio-swiper {
  width: 100%;
  height: 100%;
  box-sizing: border-box;
}

.swiper-slide {
  // @include flex-center;
  // 覆盖swiper-slide 1px的边距取整问题
  // width: calc(100% + 4px) !important;
  // will-change: transform;
  // margin-right: -1.5px;
  // margin-left: -1.5px;
  .imgs-wrap {
    width: 100%;
    height: 100%;
    .video-wrap {
      width: 100%;
      height: 100%;
      position: relative;
      .play-icon-container{
        display: flex;
        width: rem(100);
        height: rem(100);
        align-items: center;
        justify-content: center;
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        cursor: pointer;
      }
      .play-icon-wrap {
        width: rem(60);
        height: rem(60);
        cursor: pointer;
        backdrop-filter: blur(30px);
       
        background-color: #cdced573;
        display: flex;
        justify-content: center;
        align-items: center;
        border-radius: 50%;
        .play-icon {
          width: rem(20);
          height: rem(25);
          margin-left: rem(6);
        }
      }
    }
  }
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    object-position: center;

    .nio-no-objectFit & {
      width: auto;
      height: auto;
      max-width: 100%;
      max-height: 100%;
    }
  }
}

.nio-button {
  @include transition;
  transform: translate(0, -50%);
  position: absolute;
  top: 50%;
  margin: 0 rem(20);
  z-index: 2;
  opacity: 0;

  &.nio-carousel-next {
    right: 0;
  }

  .nio-carousel-img:hover & {
    opacity: 1;
    margin: 0 rem(30);
  }
}

.nio-pagination {
  @include flex-center;
  position: absolute;
  width: 100%;
  z-index: 2;

  :deep(.swiper-pagination-bullet) {
    @include transition;
    margin: 0 rem(2.5);
    width: rem(25);
    height: rem(2);
    opacity: 0.3;
    border-radius: 0;
    background-color: $color-l3;

    .nio-dark & {
      background-color: #fff;
    }
  }

  :deep(.swiper-pagination-bullet-active) {
    opacity: 1;
  }
}

.nio-hide {
  .nio-swiper {
    pointer-events: none;
  }

  .nio-button,
  .nio-pagination {
    display: none;
  }
}
</style>
